import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useFlags from '@/hooks/useFlags';
import cn from '@/utils/cn';
import { SPANavSlot } from '../../shared/SPANav';
import SearchLanding from './SearchLanding';
import UserDetailPanel from './UserDetailPanel';
import UserResultsList, { selectionKey } from './UserResultsList';
import UserSearchBar from './UserSearchBar';
import { useUserSearch } from './hooks';
import type {
  SearchField, SearchRole, SearchSubmission, UserSearchRow, UserType,
} from './types';

export type UserSearchLayout = 'rail' | 'split';

// Split layout (the legacy /admin/user_search mount, which has no shell rail): the search pane
// stretches the full column height and its inner wrapper is what sticks and scrolls.
const SPLIT_PANE_CLASSES = cn(
  'tw-box-border tw-w-96 tw-shrink-0 tw-border-0 tw-border-r tw-border-solid',
  'tw-border-spa-border tw-bg-spa-bg-secondary',
);
const SPLIT_PANE_INNER_CLASSES = 'tw-sticky tw-top-0 tw-box-border tw-max-h-screen tw-overflow-y-auto tw-px-4 tw-py-6';

// Reflow (WCAG 1.4.10): panes stack below lg so nothing scrolls sideways at 400% zoom.
const REFLOW_PANE_CLASSES = cn(
  'tw-box-border tw-w-full tw-shrink-0 tw-border-0 tw-border-b tw-border-solid',
  'tw-border-spa-border tw-bg-spa-bg-secondary lg:tw-w-96 lg:tw-border-b-0 lg:tw-border-r',
);
const REFLOW_PANE_INNER_CLASSES = cn(
  'tw-box-border tw-px-4 tw-py-6 lg:tw-sticky lg:tw-top-0 lg:tw-max-h-screen lg:tw-overflow-y-auto',
);

const SPLIT_ROOT_CLASSES = 'tw-box-border tw-flex tw-min-h-[calc(100vh-5.875rem)] tw-bg-spa-bg tw-text-spa-on-bg';

const DEFAULT_PER_TYPE = 25;
// Mirrors Support::GlobalUserSearch::MAX_PER_TYPE — the "Show all matches" ceiling.
const MAX_PER_TYPE = 500;

const parseSelection = (value: string | null) => {
  const [userType, id] = (value ?? '').split(':');

  if (!['staffer', 'student'].includes(userType) || !/^\d+$/.test(id ?? '')) return null;

  return { id, userType: userType as UserType };
};

interface UserSearchPageProps {
  layout: UserSearchLayout;
}

// AIDEV-NOTE: One page, two shells. Inside the SPA the search pane drills the shell rail
// (SPANavSlot) and the detail fills the standard container; on the legacy mount there is no rail,
// so the page keeps its own split layout. All state lives in the URL under the same params on
// both, so links and impersonation return paths work on either mount.
const UserSearchPage = ({ layout }: UserSearchPageProps) => {
  const { accessibilityReflow } = useFlags();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const field = (searchParams.get('field') ?? 'username') as SearchField;
  const role = (searchParams.get('role') ?? 'student') as SearchRole;
  const exact = searchParams.get('exact') !== 'false';
  const selection = parseSelection(searchParams.get('selected'));
  const [perType, setPerType] = useState(DEFAULT_PER_TYPE);

  const search = useUserSearch({
    exact, field, perType, q, role,
  });

  const firstRow = search.data?.data[0];
  // keepPreviousData keeps stale rows on screen during a fetch; that's right for "Show more
  // matches" (perType grew) but a fresh submission (perType back at the default) should read
  // as searching, not as the old results.
  const searching = search.isLoading
    || (search.isFetching && search.isPreviousData && perType === DEFAULT_PER_TYPE);

  useEffect(() => {
    if (selection || !firstRow) return;

    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('selected', selectionKey(firstRow));
      return next;
    }, { replace: true });
  }, [firstRow, selection, setSearchParams]);

  const handleSearch = (submission: SearchSubmission) => {
    setPerType(DEFAULT_PER_TYPE);
    const next = new URLSearchParams();

    if (submission.q) next.set('q', submission.q);
    next.set('field', submission.field);
    next.set('role', submission.role);
    next.set('exact', String(submission.exact));

    setSearchParams(next);
  };

  const handleSelect = (row: UserSearchRow) => {
    const next = new URLSearchParams(searchParams);
    next.set('selected', selectionKey(row));
    setSearchParams(next);
  };

  const handleSelectRecent = (recentType: UserType, recentId: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('selected', `${recentType}:${recentId}`);
    setSearchParams(next);
  };

  const searchPane = (
    <>
      <UserSearchBar
        exact={exact}
        field={field}
        query={q}
        role={role}
        onSearch={handleSearch}
      />
      <UserResultsList
        exact={exact}
        field={field}
        maxResults={perType >= MAX_PER_TYPE}
        q={q}
        search={search}
        searching={searching}
        selectedKey={selection && `${selection.userType}:${selection.id}`}
        onSelect={handleSelect}
        onShowAll={() => setPerType(MAX_PER_TYPE)}
      />
    </>
  );

  const detail = selection ? (
    <UserDetailPanel
      key={`${selection.userType}:${selection.id}`}
      id={selection.id}
      userType={selection.userType}
    />
  ) : (
    <SearchLanding onSelect={handleSelectRecent} />
  );

  if (layout === 'rail') {
    return (
      <>
        <SPANavSlot header={{ backLabel: 'Support', backTo: '/support', title: 'User Search' }}>
          <div className="tw-box-border tw-px-2 tw-pb-3">{searchPane}</div>
        </SPANavSlot>
        <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
          <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">{detail}</div>
        </div>
      </>
    );
  }

  return (
    <div className={cn(SPLIT_ROOT_CLASSES, accessibilityReflow && 'tw-flex-col lg:tw-flex-row')}>
      <aside className={accessibilityReflow ? REFLOW_PANE_CLASSES : SPLIT_PANE_CLASSES}>
        <div className={accessibilityReflow ? REFLOW_PANE_INNER_CLASSES : SPLIT_PANE_INNER_CLASSES}>
          <h1 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-spa-heading">User Search</h1>
          <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-spa-secondary">
            Find any user or order across all subscribers.
          </p>
          {searchPane}
        </div>
      </aside>
      <main className="tw-min-w-0 tw-flex-1">
        <div className="tw-mx-auto tw-max-w-6xl tw-px-6 tw-py-4">{detail}</div>
      </main>
    </div>
  );
};

export default UserSearchPage;
